import * as Yup from 'yup';
import { Shape } from '../yup_types';
import { CONFIRM_PASSWORD_FORM_FIELDS } from '../confirmAccount/confirm-account-form';
import { PASSWORD_REG_EXP } from '../../components/auth/PasswordStrength/PasswordStrength';

export enum SIGN_UP_FORM_FIELD {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'PrimaryContactEmail',
  PHONE = 'phone',
  COMPANY_NAME = 'legalEntityName',
  WEBSITE = 'urls',
  STRIPE = 'stripeAgreeToTerms',
  NEW_PASSWORD = 'newPassword',
  CONFIRM_PASSWORD = 'confirmPassword'
}

export enum SIGN_UP_FORM_FIELD_NAME {
  FIRST_NAME = 'First name',
  LAST_NAME = 'Last name',
  EMAIL = 'Email',
  PHONE = 'Phone',
  COMPANY_NAME = 'Company name',
  WEBSITE = 'Website url',
  STRIPE = 'stripeAgreeToTerms',
  NEW_PASSWORD = 'CONFIRM_PASSWORD.NEW_PASSWORD_PLACEHOLDER',
  CONFIRM_PASSWORD = 'CONFIRM_PASSWORD.CONFIRM_NEW_PASSWORD_PLACEHOLDER'
}

export type SignUpFieldsTypes = {
  [SIGN_UP_FORM_FIELD.FIRST_NAME]: string,
  [SIGN_UP_FORM_FIELD.LAST_NAME]: string,
  [SIGN_UP_FORM_FIELD.EMAIL]: string,
  [SIGN_UP_FORM_FIELD.PHONE]: string | number,
  [SIGN_UP_FORM_FIELD.COMPANY_NAME]: string,
  [SIGN_UP_FORM_FIELD.WEBSITE]: string,
  [SIGN_UP_FORM_FIELD.STRIPE]: boolean,
}

export type SignUpReturnFieldsTypes = {
  firstName: string,
  lastName: string,
  email: string,
  phone: string | number,
  legalEntityName: string,
  urls: string,
  recaptchaToken?: any,
}

export type StripeSignUpReturnFieldsTypes = {
  firstName: string,
  lastName: string,
  email: string,
  phone: string | number,
  legalEntityName: string,
  urls: string,
  recaptchaToken?: any,
  stripeAgreeToTerms?: boolean,
  password: string,
  confirmPassword: string,
}

export const getSignUpDetailSchema = (t): Yup.SchemaOf<SignUpFieldsTypes> => Yup.object<Shape<SignUpFieldsTypes>>().shape ({
  [SIGN_UP_FORM_FIELD.FIRST_NAME]: Yup.string()
    .required('First Name is required'),
  [SIGN_UP_FORM_FIELD.LAST_NAME]: Yup.string()
    .required('Last Name is required'),
  [SIGN_UP_FORM_FIELD.EMAIL]: Yup.string()
    .email(t('LOGIN.EMAIL_VALIDATION_CONDITION1'))
    .required(t('LOGIN.EMAIL_REQUIRED')),
  [SIGN_UP_FORM_FIELD.PHONE]: Yup.number()
    .test('is-valid-phone', t('NEW_APPLICATION.VALIDATION.VALID_PHONE'), (value) => {
      if (!value) return true;
      const phoneRegex = /^[0-9]{10,15}$/;
      return phoneRegex.test(value.toString());
    })
    .required('Phone is required'),
  [SIGN_UP_FORM_FIELD.COMPANY_NAME]: Yup.string()
    .required('Company Name is required'),
  [SIGN_UP_FORM_FIELD.WEBSITE]: Yup.string()
    .url(t('NEW_APPLICATION.VALIDATION.WEB_URL_FORMAT'))
    .required('Website url is required'),
    [SIGN_UP_FORM_FIELD.STRIPE]: Yup.boolean(),
});

export const getStripeSignUpDetailSchema = (t): Yup.SchemaOf<SignUpFieldsTypes> => Yup.object<Shape<SignUpFieldsTypes>>().shape ({
  [SIGN_UP_FORM_FIELD.FIRST_NAME]: Yup.string()
      .required('First Name is required'),
  [SIGN_UP_FORM_FIELD.LAST_NAME]: Yup.string()
      .required('Last Name is required'),
  [SIGN_UP_FORM_FIELD.EMAIL]: Yup.string()
      .email(t('LOGIN.EMAIL_VALIDATION_CONDITION1'))
      .required(t('LOGIN.EMAIL_REQUIRED')),
  [SIGN_UP_FORM_FIELD.PHONE]: Yup.number()
      .test('is-valid-phone', t('NEW_APPLICATION.VALIDATION.VALID_PHONE'), (value) => {
        if (!value) return true;
        const phoneRegex = /^[0-9]{10,15}$/;
        return phoneRegex.test(value.toString());
      })
      .required('Phone is required'),
  [SIGN_UP_FORM_FIELD.COMPANY_NAME]: Yup.string()
      .required('Company Name is required'),
  [SIGN_UP_FORM_FIELD.WEBSITE]: Yup.string()
      .url(t('NEW_APPLICATION.VALIDATION.WEB_URL_FORMAT'))
      .required('Website url is required'),
  [SIGN_UP_FORM_FIELD.STRIPE]: Yup.boolean(),
  [SIGN_UP_FORM_FIELD.NEW_PASSWORD]: Yup.string()
      .matches(PASSWORD_REG_EXP.latter, t('CONFIRM_PASSWORD.NEW_PASSWORD_CONDITION1'))
      .matches(PASSWORD_REG_EXP.uppercase, t('CONFIRM_PASSWORD.NEW_PASSWORD_CONDITION2'))
      .matches(PASSWORD_REG_EXP.number, t('CONFIRM_PASSWORD.NEW_PASSWORD_CONDITION3'))
      .matches(PASSWORD_REG_EXP.symbol, t('CONFIRM_PASSWORD.NEW_PASSWORD_CONDITION4'))
      .matches(PASSWORD_REG_EXP.complete, t('CONFIRM_PASSWORD.NEW_PASSWORD_CONDITION5'))
      .min(8, t('CONFIRM_PASSWORD.NEW_PASSWORD_CONDITION6'))
      .max(50, t('CONFIRM_PASSWORD.NEW_PASSWORD_CONDITION7'))
      .required(t('CONFIRM_PASSWORD.NEW_PASSWORD_REQUIRED')),
  [SIGN_UP_FORM_FIELD.CONFIRM_PASSWORD]: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], t('CONFIRM_PASSWORD.PASSWORD_MATCH'))
      .required(t('CONFIRM_PASSWORD.CONFIRM_PASSWORD_REQUIRED')),
});

export const initialSignUpValue = {
  [SIGN_UP_FORM_FIELD.FIRST_NAME]: '',
  [SIGN_UP_FORM_FIELD.LAST_NAME]: '',
  [SIGN_UP_FORM_FIELD.EMAIL]: '',
  [SIGN_UP_FORM_FIELD.PHONE]: '',
  [SIGN_UP_FORM_FIELD.COMPANY_NAME]: '',
  [SIGN_UP_FORM_FIELD.WEBSITE]: '',
  [SIGN_UP_FORM_FIELD.STRIPE]: false,
};

export const initialStripeSignUpValue = {
  [SIGN_UP_FORM_FIELD.FIRST_NAME]: '',
  [SIGN_UP_FORM_FIELD.LAST_NAME]: '',
  [SIGN_UP_FORM_FIELD.EMAIL]: '',
  [SIGN_UP_FORM_FIELD.PHONE]: '',
  [SIGN_UP_FORM_FIELD.COMPANY_NAME]: '',
  [SIGN_UP_FORM_FIELD.WEBSITE]: '',
  [SIGN_UP_FORM_FIELD.STRIPE]: false,
  [CONFIRM_PASSWORD_FORM_FIELDS.NEW_PASSWORD]: '',
  [CONFIRM_PASSWORD_FORM_FIELDS.CONFIRM_PASSWORD]: '',
};

export const getFormattedSignUpFormField = (formikValues): SignUpReturnFieldsTypes => ({
  firstName: formikValues?.[SIGN_UP_FORM_FIELD.FIRST_NAME],
  lastName: formikValues?.[SIGN_UP_FORM_FIELD.LAST_NAME],
  email: formikValues?.[SIGN_UP_FORM_FIELD.EMAIL],
  phone: formikValues?.[SIGN_UP_FORM_FIELD.PHONE],
  legalEntityName: formikValues?.[SIGN_UP_FORM_FIELD.COMPANY_NAME],
  urls: formikValues?.[SIGN_UP_FORM_FIELD.WEBSITE],
});

export const getStripeFormattedSignUpFormField = (formikValues): StripeSignUpReturnFieldsTypes => ({
  firstName: formikValues?.[SIGN_UP_FORM_FIELD.FIRST_NAME],
  lastName: formikValues?.[SIGN_UP_FORM_FIELD.LAST_NAME],
  email: formikValues?.[SIGN_UP_FORM_FIELD.EMAIL],
  phone: formikValues?.[SIGN_UP_FORM_FIELD.PHONE],
  legalEntityName: formikValues?.[SIGN_UP_FORM_FIELD.COMPANY_NAME],
  urls: formikValues?.[SIGN_UP_FORM_FIELD.WEBSITE],
  stripeAgreeToTerms: formikValues?.[SIGN_UP_FORM_FIELD.STRIPE],
  password: formikValues?.[CONFIRM_PASSWORD_FORM_FIELDS.NEW_PASSWORD],
  confirmPassword: formikValues?.[CONFIRM_PASSWORD_FORM_FIELDS.NEW_PASSWORD],
});
